import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { accountAtTime, fetchAccountState, type AccountState } from "./account";

const UNKNOWN: AccountState = { access: true, status: "unknown" };
const GRANTED: AccountState = { access: true, status: "granted" };

export function useAccount(supabase: SupabaseClient | null, userId: string | undefined, enabled: boolean) {
  const [snapshot, setSnapshot] = useState<{ userId: string; state: AccountState } | null>(null);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!supabase || !userId || !enabled) return;
    let cancelled = false;
    let loading = false;
    const refresh = async () => {
      setNow(Date.now());
      if (loading) return;
      loading = true;
      const state = await fetchAccountState(supabase);
      loading = false;
      if (!cancelled) setSnapshot(current => state.status === "unknown" && current?.userId === userId
        ? current : { userId, state });
    };
    const resume = () => { if (document.visibilityState === "visible") void refresh(); };
    void refresh();
    const ticker = window.setInterval(() => setNow(Date.now()), 60_000);
    const poll = window.setInterval(resume, 5 * 60_000);
    window.addEventListener("focus", resume);
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      cancelled = true;
      window.clearInterval(ticker);
      window.clearInterval(poll);
      window.removeEventListener("focus", resume);
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [supabase, userId, enabled]);
  // Wake at expiry without re-rendering the entire learning workspace every second.
  useEffect(() => {
    if (!enabled || snapshot?.userId !== userId) return;
    const end = snapshot?.state.endsAt ? Date.parse(snapshot.state.endsAt) : NaN;
    const delay = end - Date.now();
    if (!Number.isFinite(delay) || delay <= 0 || delay > 2_147_483_647) return;
    const timeout = window.setTimeout(() => setNow(Date.now()), delay);
    return () => window.clearTimeout(timeout);
  }, [enabled, snapshot, userId]);
  return enabled ? accountAtTime(snapshot && snapshot.userId === userId ? snapshot.state : UNKNOWN, now) : GRANTED;
}
